import { IClinic } from '../../interfaces/clinic';
import { LeadSystemSource } from '../../interfaces/onboarding';

/**
 * The program / cohort a referral is enrolled into, carried on `systemSource` so it reaches
 * the CRM, the funnel and scheduling without a pilot-specific field. Add a value here (and
 * to `LeadSystemSource`) to onboard the next program.
 *
 * Shared by the referral creation form and the referral detail tab. It lives here rather
 * than in either component because it has to stay in step with core's
 * `CrmConstants::SYSTEM_SOURCES`, and a second copy is a second thing to forget.
 */
export const PROGRAM_COHORT_OPTIONS: Array<{ label: string; value: LeadSystemSource }> = [
  { label: 'Referral', value: LeadSystemSource.PartnerReferral },
  { label: 'Community Referral', value: LeadSystemSource.CommunityReferral },
  { label: 'Enara Maintain', value: LeadSystemSource.MaintainPartnerReferral },
];

/**
 * Cohorts a clinic must be set up for before they can be picked. Kept in step with core's
 * `SYSTEM_SOURCES_REQUIRING_CLINIC_OPT_IN`; anything absent stays available everywhere.
 */
export const COHORTS_REQUIRING_CLINIC_OPT_IN: string[] = [
  LeadSystemSource.MaintainPartnerReferral,
];

/**
 * Core spells the stored values in title case ("Maintain partner referral") while the
 * frontend works in kebab case, so comparisons normalise rather than matching literally.
 */
export const normalizeSystemSource = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_]+/g, '-');

/**
 * Whether a cohort can be picked for this clinic.
 *
 * Disabled rather than hidden on purpose: someone who expects Enara Maintain and does not
 * find it needs to see that it exists and is not enabled here, otherwise the option looks
 * missing and they go looking for a bug.
 *
 * With no clinic chosen yet, restricted cohorts stay disabled: nothing has said they apply.
 */
export const isCohortEnabledForClinic = (
  option: { value: LeadSystemSource },
  clinic?: IClinic | null,
): boolean => {
  if (!COHORTS_REQUIRING_CLINIC_OPT_IN.includes(option.value)) {
    return true;
  }

  const supported = (clinic?.referral_supported_system_sources ?? []).map(normalizeSystemSource);

  return supported.includes(normalizeSystemSource(option.value));
};

/**
 * The label for a stored cohort value.
 *
 * Falls back to the raw value rather than to "Referral": a value we do not recognise is a
 * real difference worth showing, and quietly relabelling it as the default would hide a
 * referral tagged with something this list has not caught up to.
 */
export const cohortLabel = (systemSource?: string | null): string => {
  if (!systemSource) {
    return '—';
  }

  const normalized = normalizeSystemSource(systemSource);
  const match = PROGRAM_COHORT_OPTIONS.find(
    (option) => normalizeSystemSource(option.value) === normalized,
  );

  return match?.label ?? systemSource;
};
